import { Component, inject, computed, signal, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';
import { switchMap, catchError, tap, map } from 'rxjs/operators';
import { of } from 'rxjs';
import { PreventionGroupHeaderComponent } from '../../component/prevention-group-header/prevention-group-header.component';
import {
  PreventionGroupService,
  OpportunityDetailView,
  OpportunityGapReasonCount,
  OpportunitySiteRow,
  OpportunityLinkedOffer,
  OpportunityActionCode,
} from '../../core/services/prevention.service';

/** Libellés i18n indexés par le CODE de raison fourni par l'API (le libellé reste une affaire de front, le code et l'action une affaire de serveur). Un code inconnu retombe sur un libellé générique -- jamais le code technique. */
const GAP_REASON_KEYS: Record<string, string> = {
  NO_BILAN_MAPPING: 'opportunites.gapReason.NO_BILAN_MAPPING',
  BILAN_AVAILABLE_NO_OFFER: 'opportunites.gapReason.BILAN_AVAILABLE_NO_OFFER',
  OFFER_UNAVAILABLE_ON_SITE: 'opportunites.gapReason.OFFER_UNAVAILABLE_ON_SITE',
  UNCLASSIFIED: 'opportunites.gapReason.UNCLASSIFIED',
  NO_OFFER_CONFIGURED_ON_SITE: 'opportunites.gapReason.NO_OFFER_CONFIGURED_ON_SITE',
  NOT_PRESENTABLE: 'opportunites.gapReason.NOT_PRESENTABLE',
};
const GAP_REASON_FALLBACK_KEY = 'opportunites.gapReason.OTHER';

/**
 * CTA « data -> action » : la CIBLE de chaque `action` fournie par le serveur, sur des écrans Catalogue
 * DÉJÀ existants (jamais un nouveau workflow). L'action n'est JAMAIS déduite de la raison : elle vient de
 * l'API (`gapReasons[].action`, `sites[].topGapAction`). `NONE` (ou une action inconnue) = aucun CTA.
 * Le détail est Groupe-scopé : aucun établissement précis n'est en contexte, DEPLOYMENT/VERIFY renvoient
 * donc vers « Mes Offres » (vue Groupe), d'où le laboratoire choisit l'offre/le site à traiter.
 */
const ACTION_ROUTES: Partial<Record<OpportunityActionCode, string>> = {
  DEPLOYMENT: '/prevention-offres',
  CONFIGURE: '/prevention-offres/nouvelle',
  LIBRARY: '/prevention-offres/bibliotheque',
  VERIFY: '/prevention-offres',
};

const ACTION_LABEL_KEYS: Partial<Record<OpportunityActionCode, string>> = {
  DEPLOYMENT: 'opportunites.action.DEPLOYMENT',
  CONFIGURE: 'opportunites.action.CONFIGURE',
  LIBRARY: 'opportunites.action.LIBRARY',
  VERIFY: 'opportunites.action.VERIFY',
};

/**
 * Détail d'une opportunité de prévention (lot L6) -- accessible depuis une ligne de {@code Prévention >
 * Opportunités de prévention}. Consomme {@link PreventionGroupService#getOpportunityDetail} et RESTITUE
 * STRICTEMENT le contrat backend : aucun recalcul de KPI/taux, aucun argmax de raison, aucune déduction
 * d'action ou d'origine, aucune fusion de sujets.
 * <p>{@code subjectKey} (paramètre de route) est OPAQUE : décodée par le routeur, renvoyée telle quelle à
 * l'API (encodée par le service), jamais parsée ni affichée. Le titre est {@code subjectLabel}.
 * {@code subjectOrigin} n'est ni affichée ni utilisée. Répond à 3 questions : pourquoi cette opportunité
 * reste sans réponse, quels sites sont les plus concernés, quelle action prendre.
 */
@Component({
  selector: 'app-prevention-besoin-detail',
  standalone: true,
  imports: [TranslatePipe, RouterLink, PreventionGroupHeaderComponent],
  templateUrl: './prevention-besoin-detail.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./prevention-besoin-detail.component.scss'],
})
export class PreventionBesoinDetailComponent {
  private readonly groupSvc = inject(PreventionGroupService);
  private readonly translate = inject(TranslateService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly periodDays = signal(30);
  readonly notFound = signal(false);
  readonly loadError = signal(false);

  private readonly routeSubjectKey = toSignal(
    this.route.paramMap.pipe(map(params => params.get('subjectKey'))),
    { initialValue: null as string | null }
  );

  private readonly trigger = computed(() => ({ subjectKey: this.routeSubjectKey(), days: this.periodDays() }));

  readonly detail = toSignal(
    toObservable(this.trigger).pipe(
      tap(() => { this.notFound.set(false); this.loadError.set(false); this.showAllOffers.set(false); }),
      switchMap(({ subjectKey, days }) => {
        if (!subjectKey) return of(null);
        return this.groupSvc.getOpportunityDetail(subjectKey, days).pipe(
          catchError((err: HttpErrorResponse) => {
            // 404 : sujet inconnu ; 400 : clé malformée (seul paramètre saisissable par l'utilisateur, via l'URL) -- même état « Sujet introuvable ».
            if (err.status === 404 || err.status === 400) this.notFound.set(true);
            else this.loadError.set(true);
            return of(null);
          })
        );
      })
    ),
    { initialValue: null as OpportunityDetailView | null }
  );

  /** Ni {@code noData} ni {@code insufficientData}, et plus rien à couvrir -- le bloc « Pourquoi... » n'a alors aucune raison d'être affiché vide. */
  readonly fullyCovered = computed(() => {
    const d = this.detail();
    return !!d && !d.noData && !d.insufficientData && (d.uncoveredCount ?? 0) === 0 && (d.resolvedCoverageCount ?? 0) > 0;
  });

  /** Raisons de non-couverture, dans l'ordre et avec les actions fournis par l'API (jamais re-triées ni recalculées). */
  readonly reasons = computed<OpportunityGapReasonCount[]>(() => this.detail()?.gapReasons ?? []);

  /** Toutes les lignes {@code sites[]} existent mais sont masquées (petite cohorte) : un état simple plutôt qu'un tableau rempli de « Données insuffisantes ». Dès qu'UN site est exploitable, le tableau normal reprend. */
  readonly allSitesMasked = computed(() => {
    const sites = this.detail()?.sites ?? [];
    return sites.length > 0 && sites.every(s => s.insufficientData);
  });

  /** Réduction de densité initiale UNIQUEMENT (aucune offre retirée ni filtrée) : triées par {@code activeSiteCount} décroissant, 3 affichées par défaut. */
  readonly sortedOffers = computed<OpportunityLinkedOffer[]>(() =>
    [...(this.detail()?.linkedOffers ?? [])].sort((a, b) => b.activeSiteCount - a.activeSiteCount)
  );

  readonly showAllOffers = signal(false);

  readonly visibleOffers = computed(() =>
    this.showAllOffers() ? this.sortedOffers() : this.sortedOffers().slice(0, 3)
  );

  readonly hiddenOffersCount = computed(() => this.sortedOffers().length - this.visibleOffers().length);

  setPeriodDays(days: number): void {
    this.periodDays.set(days);
  }

  showMoreOffers(): void {
    this.showAllOffers.set(true);
  }

  /** Retour vers la liste parente -- même convention « Retour = page liste immédiate » que les autres sous-pages Prévention. */
  goBack(): void {
    this.router.navigate(['/pilotage-prevention/besoins']);
  }

  gapReasonLabel(reason: string): string {
    return this.translate.instant(GAP_REASON_KEYS[reason] ?? GAP_REASON_FALLBACK_KEY);
  }

  /** UNIQUEMENT pour UNCLASSIFIED -- jamais prétendre identifier la porte exacte du résolveur. */
  gapReasonTooltip(reason: string | null): string | null {
    if (reason !== 'UNCLASSIFIED') return null;
    return this.translate.instant('opportunites.gapReason.UNCLASSIFIED_TOOLTIP');
  }

  /** Un CTA n'existe que pour une action connue fournie par l'API : `NONE`, absente ou inconnue -> aucun bouton. */
  hasAction(action: OpportunityActionCode | null | undefined): boolean {
    return !!action && ACTION_ROUTES[action] !== undefined;
  }

  actionLabel(action: OpportunityActionCode | null | undefined): string {
    return action && ACTION_LABEL_KEYS[action] ? this.translate.instant(ACTION_LABEL_KEYS[action]!) : '';
  }

  onActionClick(action: OpportunityActionCode | null | undefined): void {
    const target = action ? ACTION_ROUTES[action] : undefined;
    if (target) this.router.navigate([target]);
  }

  /** « 6 opportunités » : le volume brut fourni par l'API -- aucune part en % calculée côté front. */
  reasonVolumeLabel(r: OpportunityGapReasonCount): string {
    return this.translate.instant(
      r.count > 1 ? 'opportunites.detail.reasons.volumePlural' : 'opportunites.detail.reasons.volumeSingular',
      { count: r.count }
    );
  }

  offerCtaLabel(offer: OpportunityLinkedOffer): string {
    return this.translate.instant(offer.activeSiteCount > 0
      ? 'opportunites.detail.offers.manageDeployment'
      : 'opportunites.detail.offers.configure');
  }

  /** Route existante `prevention-offres/:offerId/sites` (déploiement) ou `prevention-offres/:offerId` (édition) -- jamais un nouvel écran. */
  onOfferCtaClick(offer: OpportunityLinkedOffer): void {
    if (offer.activeSiteCount > 0) {
      this.router.navigate(['/prevention-offres', offer.offerId, 'sites']);
    } else {
      this.router.navigate(['/prevention-offres', offer.offerId]);
    }
  }

  // ───────────────────────── Formatage (jamais 0 / 0 % / valeur reconstruite pour un `null`) ─────────────────────────

  formatRate(rate: number | null | undefined): string {
    if (rate == null) return this.translate.instant('opportunites.states.insufficientData');
    return Math.round(rate * 100) + '%';
  }

  formatCount(value: number | null | undefined): string {
    if (value == null) return this.translate.instant('opportunites.states.insufficientData');
    return String(value);
  }

  /** Taux `null` sur un sujet/site NON masqué : rien n'est encore résolu -- « Couverture non disponible », jamais un problème de cohorte (déjà tranché en amont par `insufficientData`). */
  formatResolvedRate(rate: number | null | undefined): string {
    if (rate == null) return this.translate.instant('opportunites.states.coverageUnavailable');
    return Math.round(rate * 100) + '%';
  }

  trackSite(_index: number, row: OpportunitySiteRow): string {
    return row.establishmentId;
  }

  trackOffer(_index: number, row: OpportunityLinkedOffer): string {
    return row.offerId;
  }
}
